// web/src/api/health.ts
//
// Liveness check for the upstream news-analyzer-api. Used by
// <UpstreamHealthBanner /> to surface an outage banner across the
// authenticated app shell.
//
// The endpoint at /health is unauthenticated and currently returns a
// free-form 200 body — we treat it as a binary liveness signal. Anything
// other than a 2xx response is read as "service unreachable" and
// triggers the banner.
import { useQuery } from "@tanstack/react-query";
import { apiFetch, ApiError } from "./client";

export type HealthStatus = "ok" | "unreachable" | "unknown";

export const HEALTH_QUERY_KEY = ["upstream", "health"] as const;

export function useUpstreamHealth() {
  const query = useQuery<unknown, ApiError>({
    queryKey: HEALTH_QUERY_KEY,
    queryFn: () => apiFetch<unknown>("/health"),
    // Poll every 30s. Production-grade defense: surface a banner within
    // ~30s of upstream going down without burning the rate limit. We
    // also refetch on window focus so the user gets an immediate signal
    // when they tab back in.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
    staleTime: 15_000,
  });

  let status: HealthStatus = "unknown";
  if (query.isError) status = "unreachable";
  else if (query.isSuccess) status = "ok";

  return { status, isLoading: query.isLoading };
}
